import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import dayjs from "dayjs";
import { Button, DatePicker, Empty, Input, Modal, Pagination, Popconfirm, Spin, Tag, Typography, message } from "antd";
import { DeleteOutlined, DownloadOutlined, EyeOutlined, ReloadOutlined, SearchOutlined } from "@ant-design/icons";
import PageTitle from "../../components/title/pageTitle";
import Navigation from "../../components/navigation/navigation";
import Footer from "../../components/footer/footer";
import BackHomeIcon, { PANEL_PATH } from "../../components/icons/BackHome/backhome";
import copyToClipboard from "../../utils/copy";
import SelectionPhotoPreview from "../../components/selection/SelectionPhotoPreview";
import { deleteSubmission, getSubmission, listSubmissions, SUBMISSIONS_DEFAULT_PAGE_SIZE, SUBMISSIONS_MAX_PAGE_SIZE } from "../../utils/selectionAdminApi";
import { downloadDeliveryFile } from "../../utils/deliveryDownload";
import { getProxyDownloadUrl } from "../../utils/deliveryApi";
import { normalizeAlbumPath, toDeliveryPublicUrl, toThumbKey } from "../../utils/deliveryPath";
import { useTranslation } from "../../i18n";
import "./selection.css";

const { Paragraph, Text } = Typography;

function pickFullKey(albumPath, pickPath) {
    const root = normalizeAlbumPath(albumPath);
    const rel = normalizeAlbumPath(pickPath);
    if (!rel) {
        return "";
    }
    return root ? `${root}/${rel}` : rel;
}

function pickThumbUrl(albumPath, pickPath) {
    const fullKey = pickFullKey(albumPath, pickPath);
    if (!fullKey) {
        return null;
    }
    return toDeliveryPublicUrl(toThumbKey(fullKey));
}

function pickPreviewFile(albumPath, pick) {
    const id = pickFullKey(albumPath, pick.path);
    return {
        id,
        name: pick.name || pick.path,
        path: pick.path,
        note: pick.note || "",
        thumbnailUrl: pickThumbUrl(albumPath, pick.path),
        downloadUrl: id ? toDeliveryPublicUrl(id) : null,
        proxyDownloadUrl: id ? getProxyDownloadUrl(id, normalizeAlbumPath(albumPath)) : null,
    };
}

function escapeCsvCell(value) {
    const str = value == null ? "" : String(value);
    if (/[",\n\r]/.test(str)) {
        return `"${str.replace(/"/g, '""')}"`;
    }
    return str;
}

function downloadCsv(filename, content) {
    // Prepend BOM so Excel detects UTF-8 and renders non-ASCII characters correctly.
    const blob = new Blob(["\uFEFF", content], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
}

function groupSubmissionsByAlbum(items) {
    const groups = new Map();
    for (const item of items) {
        const albumPath = normalizeAlbumPath(item.albumPath);
        const key = albumPath || "__unknown__";
        if (!groups.has(key)) {
            groups.set(key, {
                key,
                albumPath,
                albumName: item.albumName || "",
                items: [],
            });
        }
        const group = groups.get(key);
        group.items.push(item);
        if (!group.albumName && item.albumName) {
            group.albumName = item.albumName;
        }
    }
    return Array.from(groups.values()).sort((a, b) => {
        const latest = (group) =>
            Math.max(...group.items.map((entry) => entry.createdAt || 0), 0);
        return latest(b) - latest(a);
    });
}

function SelectionResults() {
    const { t } = useTranslation();
    const [submissions, setSubmissions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [viewing, setViewing] = useState(null);
    const [viewingLoading, setViewingLoading] = useState(false);
    const [previewFile, setPreviewFile] = useState(null);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(SUBMISSIONS_DEFAULT_PAGE_SIZE);
    const [hasMore, setHasMore] = useState(false);
    const [searchText, setSearchText] = useState("");
    const [dateRange, setDateRange] = useState(null);
    const [appliedQuery, setAppliedQuery] = useState({ q: "", from: null, to: null });
    const detailCacheRef = useRef(new Map());

    const loadSubmissions = useCallback(async () => {
        setLoading(true);
        try {
            const result = await listSubmissions({
                q: appliedQuery.q || undefined,
                from: appliedQuery.from ?? undefined,
                to: appliedQuery.to ?? undefined,
                page,
                pageSize,
            });
            setSubmissions(result.submissions);
            setHasMore(result.hasMore);
        } catch {
            message.error(t("selection.results.loadFailed"));
        } finally {
            setLoading(false);
        }
    }, [appliedQuery, page, pageSize, t]);

    useEffect(() => {
        loadSubmissions();
    }, [loadSubmissions]);

    const handleSearch = useCallback(() => {
        const q = searchText.trim();
        const from = dateRange?.[0] ? dateRange[0].startOf("day").unix() : null;
        const to = dateRange?.[1] ? dateRange[1].endOf("day").unix() : null;
        setAppliedQuery({ q, from, to });
        setPage(1);
    }, [dateRange, searchText]);

    const handleResetFilters = useCallback(() => {
        setSearchText("");
        setDateRange(null);
        setAppliedQuery({ q: "", from: null, to: null });
        setPage(1);
    }, []);

    const fetchSubmissionDetail = useCallback(async (id) => {
        const cached = detailCacheRef.current.get(id);
        if (cached) {
            return cached;
        }
        const detail = await getSubmission(id);
        if (detail) {
            detailCacheRef.current.set(id, detail);
        }
        return detail;
    }, []);

    const handleDelete = useCallback(
        async (id) => {
            try {
                await deleteSubmission(id);
                message.success(t("selection.results.deleteDone"));
                detailCacheRef.current.delete(id);
                if (viewing?.id === id) {
                    setViewing(null);
                    setPreviewFile(null);
                }
                loadSubmissions();
            } catch {
                message.error(t("selection.results.deleteFailed"));
            }
        },
        [loadSubmissions, t, viewing]
    );

    const openDetail = useCallback(
        async (item) => {
            setViewingLoading(true);
            setViewing(null);
            setPreviewFile(null);
            try {
                const detail = await fetchSubmissionDetail(item.id);
                if (!detail) {
                    message.error(t("selection.results.detailLoadFailed"));
                    return;
                }
                setViewing(detail);
            } catch {
                message.error(t("selection.results.detailLoadFailed"));
            } finally {
                setViewingLoading(false);
            }
        },
        [fetchSubmissionDetail, t]
    );

    const resolveSubmissionDetail = useCallback(
        async (submission) => {
            if (submission.picks) {
                return submission;
            }
            return fetchSubmissionDetail(submission.id);
        },
        [fetchSubmissionDetail]
    );

    const handleCopyPaths = useCallback(
        async (submission) => {
            try {
                const detail = await resolveSubmissionDetail(submission);
                const text = (detail?.picks || []).map((p) => p.path).join("\n");
                await copyToClipboard(text);
                message.success(t("selection.results.pathsCopied"));
            } catch {
                message.error(t("selection.results.copyFailed"));
            }
        },
        [resolveSubmissionDetail, t]
    );

    const handleCopyNotes = useCallback(
        async (submission) => {
            try {
                const detail = await resolveSubmissionDetail(submission);
                const lines = (detail?.picks || [])
                    .filter((p) => p.note)
                    .map((p) => `${p.name || p.path}: ${p.note}`);
                if (lines.length === 0) {
                    message.info(t("selection.results.noNotes"));
                    return;
                }
                await copyToClipboard(lines.join("\n"));
                message.success(t("selection.results.notesCopied"));
            } catch {
                message.error(t("selection.results.copyFailed"));
            }
        },
        [resolveSubmissionDetail, t]
    );

    const handleExportCsv = useCallback(
        async (submission) => {
            try {
                const detail = await resolveSubmissionDetail(submission);
                const headerRow = [
                    t("selection.results.csvIndex"),
                    t("selection.results.csvName"),
                    t("selection.results.csvPath"),
                    t("selection.results.csvNote"),
                ];
                const dataRows = (detail?.picks || []).map((pick, index) => [
                    index + 1,
                    pick.name || pick.path || "",
                    pick.path || "",
                    pick.note || "",
                ]);
                const csv = [headerRow, ...dataRows]
                    .map((row) => row.map(escapeCsvCell).join(","))
                    .join("\r\n");
                const datePart = dayjs((detail?.createdAt || submission.createdAt || 0) * 1000).format(
                    "YYYYMMDD-HHmm"
                );
                const safeName = (detail?.selectorName || submission.selectorName || "selection").replace(
                    /[\\/:*?"<>|]/g,
                    "_"
                );
                downloadCsv(`selection-${safeName}-${datePart}.csv`, csv);
                message.success(t("selection.results.exportDone"));
            } catch {
                message.error(t("selection.results.exportFailed"));
            }
        },
        [resolveSubmissionDetail, t]
    );

    const viewingThumbs = useMemo(() => {
        if (!viewing) {
            return [];
        }
        return (viewing.picks || []).map((pick, index) => {
            const file = pickPreviewFile(viewing.albumPath, pick);
            return {
                key: `${index}-${pick.path}`,
                name: file.name,
                path: pick.path,
                note: pick.note || "",
                thumb: file.thumbnailUrl,
                file,
            };
        });
    }, [viewing]);

    const handlePreviewDownload = useCallback(
        async (file) => {
            const hide = message.loading(`${t("selection.gallery.downloading")}…`, 0);
            try {
                await downloadDeliveryFile(file);
            } catch (error) {
                message.error(error?.message || t("selection.gallery.downloadFailed"));
            } finally {
                hide();
            }
        },
        [t]
    );

    const closeDetail = useCallback(() => {
        setPreviewFile(null);
        setViewing(null);
        setViewingLoading(false);
    }, []);

    const albumGroups = useMemo(() => groupSubmissionsByAlbum(submissions), [submissions]);

    const paginationTotal = hasMore
        ? page * pageSize + 1
        : (page - 1) * pageSize + submissions.length;

    const hasActiveFilters = Boolean(appliedQuery.q || appliedQuery.from || appliedQuery.to);

    return (
        <>
            <PageTitle>
                <Navigation />
            </PageTitle>
            <BackHomeIcon to={PANEL_PATH} />

            <div className="delivery-page">
                <div className="custom-h1">
                    <span>{t("selection.results.title")}</span>
                </div>
                <Paragraph
                    style={{
                        textAlign: "center",
                        color: "var(--color-text-subtle)",
                        fontSize: "0.9rem",
                        maxWidth: 560,
                        margin: "0 auto 1.5rem",
                    }}
                >
                    {t("selection.results.subtitle")}
                </Paragraph>

                <div className="delivery-admin-section">
                    <div className="selection-results__toolbar">
                        <div className="selection-results__filters">
                            <Input
                                allowClear
                                value={searchText}
                                onChange={(event) => setSearchText(event.target.value)}
                                onPressEnter={handleSearch}
                                placeholder={t("selection.results.searchPlaceholder")}
                                className="selection-results__search"
                            />
                            <DatePicker.RangePicker
                                value={dateRange}
                                onChange={setDateRange}
                                allowEmpty={[true, true]}
                                className="selection-results__date-range"
                                placeholder={[
                                    t("selection.results.dateFrom"),
                                    t("selection.results.dateTo"),
                                ]}
                            />
                            <Button type="primary" icon={<SearchOutlined />} onClick={handleSearch}>
                                {t("selection.results.search")}
                            </Button>
                            {hasActiveFilters ? (
                                <Button onClick={handleResetFilters}>
                                    {t("selection.results.resetFilters")}
                                </Button>
                            ) : null}
                        </div>
                        <Button icon={<ReloadOutlined />} onClick={loadSubmissions}>
                            {t("selection.results.refresh")}
                        </Button>
                    </div>

                    {loading ? (
                        <div style={{ textAlign: "center", padding: "1.5rem 0" }}>
                            <Spin />
                        </div>
                    ) : submissions.length === 0 ? (
                        <Empty
                            description={
                                hasActiveFilters
                                    ? t("selection.results.emptyFiltered")
                                    : t("selection.results.empty")
                            }
                        />
                    ) : (
                        <>
                            <div className="selection-results__albums">
                                {albumGroups.map((group) => (
                                    <section key={group.key} className="selection-results__album">
                                        <div className="selection-results__album-header">
                                            <div className="selection-results__album-heading">
                                                <Text strong className="selection-results__album-name">
                                                    {group.albumName ||
                                                        group.albumPath ||
                                                        t("selection.results.unknownAlbum")}
                                                </Text>
                                                <Tag>
                                                    {t("selection.results.albumSubmissionCount", {
                                                        count: group.items.length,
                                                    })}
                                                </Tag>
                                            </div>
                                            {group.albumPath ? (
                                                <Text
                                                    type="secondary"
                                                    className="selection-results__album-path"
                                                >
                                                    {group.albumPath}
                                                </Text>
                                            ) : null}
                                        </div>
                                        <div className="delivery-admin-list selection-results__album-list">
                                            {group.items.map((item) => (
                                                <div key={item.id} className="delivery-admin-list__item">
                                                    <div className="delivery-admin-list__main">
                                                        <div className="delivery-admin-list__title">
                                                            <Text strong>{item.selectorName}</Text>
                                                            <Tag color="blue">
                                                                {t("selection.results.photoCount", {
                                                                    count: item.photoCount,
                                                                })}
                                                            </Tag>
                                                        </div>
                                                        <Text
                                                            type="secondary"
                                                            className="delivery-admin-list__meta"
                                                        >
                                                            {t("selection.results.submittedAt", {
                                                                time: dayjs(item.createdAt * 1000).format(
                                                                    "YYYY-MM-DD HH:mm"
                                                                ),
                                                            })}
                                                        </Text>
                                                    </div>
                                                    <div className="delivery-admin-list__actions">
                                                        <Button
                                                            size="small"
                                                            icon={<EyeOutlined />}
                                                            onClick={() => openDetail(item)}
                                                        >
                                                            {t("selection.results.view")}
                                                        </Button>
                                                        <Button
                                                            size="small"
                                                            icon={<DownloadOutlined />}
                                                            onClick={() => handleExportCsv(item)}
                                                        >
                                                            {t("selection.results.exportCsv")}
                                                        </Button>
                                                        <Popconfirm
                                                            title={t("selection.results.deleteConfirm")}
                                                            okText={t("selection.results.deleteOk")}
                                                            cancelText={t("selection.results.deleteCancel")}
                                                            onConfirm={() => handleDelete(item.id)}
                                                        >
                                                            <Button
                                                                size="small"
                                                                danger
                                                                icon={<DeleteOutlined />}
                                                            />
                                                        </Popconfirm>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </section>
                                ))}
                            </div>
                            <div className="selection-results__pagination">
                                <Pagination
                                    current={page}
                                    pageSize={pageSize}
                                    total={paginationTotal}
                                    showSizeChanger
                                    pageSizeOptions={[10, 20, SUBMISSIONS_MAX_PAGE_SIZE]}
                                    onChange={(nextPage, nextPageSize) => {
                                        setPage(nextPage);
                                        setPageSize(nextPageSize);
                                    }}
                                    showTotal={(total, range) => {
                                        const base = t("selection.results.pageRange", {
                                            from: range[0],
                                            to: range[1],
                                        });
                                        return hasMore ? `${base}${t("selection.results.hasMoreSuffix")}` : base;
                                    }}
                                />
                            </div>
                        </>
                    )}
                </div>
            </div>

            <Modal
                open={Boolean(viewing) || viewingLoading}
                title={
                    viewing
                        ? t("selection.results.detailTitle", { name: viewing.selectorName })
                        : t("selection.results.detailLoading")
                }
                onCancel={closeDetail}
                width={720}
                footer={
                    viewing
                        ? [
                              <Button key="copy" onClick={() => handleCopyPaths(viewing)}>
                                  {t("selection.results.copyPaths")}
                              </Button>,
                              <Button key="copy-notes" onClick={() => handleCopyNotes(viewing)}>
                                  {t("selection.results.copyNotes")}
                              </Button>,
                              <Button
                                  key="export-csv"
                                  icon={<DownloadOutlined />}
                                  onClick={() => handleExportCsv(viewing)}
                              >
                                  {t("selection.results.exportCsv")}
                              </Button>,
                              <Button key="close" type="primary" onClick={closeDetail}>
                                  {t("selection.results.close")}
                              </Button>,
                          ]
                        : [
                              <Button key="close" onClick={closeDetail}>
                                  {t("selection.results.close")}
                              </Button>,
                          ]
                }
                destroyOnClose
            >
                {viewingLoading ? (
                    <div style={{ textAlign: "center", padding: "2rem 0" }}>
                        <Spin />
                    </div>
                ) : null}
                {viewing && !viewingLoading ? (
                    <>
                        <Paragraph type="secondary" style={{ fontSize: "0.82rem" }}>
                            {t("selection.results.albumLabel")}: {viewing.albumName} ({viewing.albumPath})
                        </Paragraph>
                        <div className="selection-results__grid">
                            {viewingThumbs.map((thumb) => (
                                <div
                                    key={thumb.key}
                                    className={`selection-results__item${
                                        thumb.note ? " selection-results__item--noted" : ""
                                    }`}
                                >
                                    {thumb.thumb ? (
                                        <button
                                            type="button"
                                            className="selection-results__thumb-btn"
                                            aria-label={thumb.name}
                                            onClick={() => setPreviewFile(thumb.file)}
                                        >
                                            <img
                                                src={thumb.thumb}
                                                alt={thumb.name}
                                                className="selection-results__thumb"
                                                loading="lazy"
                                                decoding="async"
                                            />
                                        </button>
                                    ) : (
                                        <div className="selection-results__thumb selection-results__thumb--empty" />
                                    )}
                                    <span className="selection-results__name">{thumb.name}</span>
                                    {thumb.note ? (
                                        <p className="selection-results__note">
                                            <span className="selection-results__note-label">
                                                {t("selection.results.note")}
                                            </span>
                                            {thumb.note}
                                        </p>
                                    ) : null}
                                </div>
                            ))}
                        </div>
                    </>
                ) : null}
            </Modal>

            <SelectionPhotoPreview
                file={previewFile}
                open={Boolean(previewFile)}
                readOnly
                note={previewFile?.note || ""}
                onClose={() => setPreviewFile(null)}
                onDownload={handlePreviewDownload}
                files={viewingThumbs.map((thumb) => thumb.file)}
                onNavigate={setPreviewFile}
            />

            <Footer />
        </>
    );
}

export default SelectionResults;
